import { ContractsPage, ContractPage } from "./pages/Contracts";
import { ItemSetupsPage, ItemSetupPage } from "./pages/ItemSetups";
import { WorkbooksPage, WorkbookPage } from "./pages/Workbooks";
import { DashboardsPage, DashboardPage } from "./pages/Dashboards";
import { CpmPage, CpmVersionPage } from "./pages/Cpm";
import { PlanningPage } from "./pages/Planning";
import { InventoryPage } from "./pages/Inventory";
import { PlanDashboardPage } from "./pages/PlanDashboard";
import { OrderPlannerPage } from "./pages/OrderPlanner";
import { ChangeOrdersPage, ChangeOrderPage, ImportCadPage } from "./pages/Plm";
import { useEffect, useState } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { config } from "./config";
import { currentAccount, signIn } from "./auth";
import { api } from "./api";
import { Mark } from "./ui";
import { Shell } from "./Shell";
import { Home } from "./pages/Home";
import { Records } from "./pages/Records";
import { NewIssue } from "./pages/NewIssue";
import { RecordPage } from "./pages/RecordPage";
import { ParetoPage } from "./pages/ParetoPage";
import { OntologyPage } from "./pages/OntologyPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { LinksPage } from "./pages/LinksPage";
import { AreaPage } from "./pages/AreaPage";
import { AccountPage, AccountsList, ImportAccounts, NewAccount } from "./pages/Accounts";
import { NewOpportunity, OpportunityPage, PipelinePage } from "./pages/Opportunities";
import { SalesAnalyticsPage } from "./pages/SalesAnalytics";
import { QuotePage } from "./pages/QuotePage";
import { ObjectTypePage, ObjectView } from "./pages/ObjectPages";
import { SimpleAppPage, SimpleAppNew, SimpleAppRecord } from "./apps/SimpleApp";
import { SearchPage } from "./pages/SearchPage";
import { WmsApp } from "./wms/WmsApp";
import { ReplenishmentPage } from "./pages/Replenishment";
import { CountsPage, CountSettingsPage, NewCountPage } from "./counts/Counts";
import { CountPage } from "./counts/CountPage";
import { NewWorkOrderPage, RequestPage, WorkOrdersPage } from "./maintenance/WorkOrders";
import { WorkOrderPage } from "./maintenance/WorkOrder";
import { AssetPage, AssetsPage, NewAssetPage } from "./maintenance/Assets";
import { NewPmPage, PmPage, PmsPage } from "./maintenance/Pms";
import { SchedulePage } from "./maintenance/Schedule";
import { HelpHome, HelpNew, HelpTicket, HelpArticle } from "./servicedesk/Help";
import { DeskPage, NewTicketPage, TicketPage } from "./servicedesk/Desk";
import { ApprovalsPage, ReportPage, SettingsPage } from "./servicedesk/Admin";
import { PortalHomePage, PortalOrdersPage, PortalPoPage, PortalSettingsPage, PortalSupplierPage, PortalSuppliersPage } from "./portal/Buyer";
import { SupplierApp } from "./portal/Supplier";
import { ContractSignPage, PublicSignPage, VerifyPage } from "./signing/SignPages";
import { ProcessMiningPage, ProcessPage, ScreensPage } from "./improve/ProcessMining";
import { CatalogPage, ProcessPage as CatalogProcessPage } from "./improve/Catalog";
import { SystemPage, SystemsPage } from "./improve/Systems";
import { RoadmapPage } from "./improve/Roadmap";
import { MeetingPage, SteeringPage } from "./improve/Steering";
import { FactoryHome, NewRequestPage as FactoryNewRequest, RequestPage as FactoryRequest, RequestsPage as FactoryRequests, TaskPage as FactoryTask, TasksPage as FactoryTasks } from "./factory/Factory";
import { PipelinePage as ReleasePipeline, TestingPage } from "./factory/Pipeline";
import { AppDocPage, DocsHome } from "./factory/Docs";
import { ProcessLibrary } from "./processes/ProcessLibrary";
import { ProcessPage as ProcessMapPage } from "./processes/ProcessEditor";
import { trackView } from "./telemetry";
import { ProductivityHome, TemplatesPage, ImportPage } from "./productivity/Home";
import { TablePage } from "./productivity/TablePage";
import { FormPage, PublicFormPage } from "./productivity/FormView";
import "./productivity/productivity.css";
import { MarketingHome } from "./marketing/Home";
import { CalendarPage } from "./marketing/Calendar";
import { CampaignPage, CampaignsPage, NewCampaignPage } from "./marketing/Campaigns";
import { ImportLeadsPage, LeadPage, LeadsPage, NewLeadPage } from "./marketing/Leads";
import { FormsPage, PublicLeadFormPage } from "./marketing/Forms";
import { PagesPage, PageEditorPage } from "./marketing/Pages";
import { EmailPage, EmailsPage } from "./marketing/Emails";
import { SequencePage, SequencesPage } from "./marketing/Sequences";
import { AudiencesPage } from "./marketing/Audiences";
import { EmailSettingsPage } from "./marketing/EmailSettings";
import { SecurityHome, RolesPage, RolePage, PeoplePage, CheckPage, ChangesPage } from "./security/Security";
import { canOpen, useAccess, stopViewAs } from "./security/store";
import { appForPath } from "./suite";
import { MyWorkPage } from "./mywork/MyWork";

/** A screen the person's roles don't open: say so plainly, instead of a page full of errors. */
function NoAccess({ path }: { path: string }) {
  const m = useAccess();
  const app = appForPath(path);
  return (
    <div className="page">
      <div className="card no-access">
        <div className="lock" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 16 16"><path d="M4.5 7V5a3.5 3.5 0 017 0v2h.5A1 1 0 0113 8v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8a1 1 0 011-1h.5zm1.5 0h4V5a2 2 0 00-4 0v2z" fill="currentColor" /></svg></div>
        <h1>{m?.viewAs ? `${m.viewAs.name} can't open ${app?.name ?? "this page"}` : `You don't have access to ${app?.name ?? "this page"}`}</h1>
        <p>{m?.viewAs ? `None of their roles (${m.roles.join(", ") || "none"}) open it.` : "Your roles don't open it. If you need it for your work, ask your administrator."}</p>
        <div className="btn-row" style={{ justifyContent: "center" }}>
          <Link className="btn primary" to="/">Go home</Link>
          {m?.viewAs && <button className="btn" onClick={() => stopViewAs()}>Stop viewing as</button>}
        </div>
      </div>
    </div>
  );
}

function SignIn() {
  return (
    <div className="signin">
      <div className="left">
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 18, fontWeight: 600 }}><Mark size={28} dark />courageMFG</div>
        <div>
          <h1>Business Central wasn't built for the plant floor.</h1>
          <p>Issues, CAPA, documents and suppliers in one linked model of the plant, run by agents and open to Claude and Copilot.</p>
        </div>
        <div style={{ color: "#8C99A6", fontSize: 12 }}>© courageMFG</div>
      </div>
      <div className="right">
        <div style={{ width: "min(360px, 100%)" }}>
          <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 600 }}>Sign in</h2>
          <p className="muted" style={{ margin: "0 0 20px" }}>Use your work Microsoft account.</p>
          <button className="btn primary" style={{ width: "100%", height: 40, justifyContent: "center" }} onClick={() => signIn()}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><rect x="1" y="1" width="6.5" height="6.5" fill="#F25022" /><rect x="8.5" y="1" width="6.5" height="6.5" fill="#7FBA00" /><rect x="1" y="8.5" width="6.5" height="6.5" fill="#00A4EF" /><rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#FFB900" /></svg>
            Sign in with Microsoft
          </button>
        </div>
      </div>
    </div>
  );
}

function Guarded({ children }: { children: React.ReactNode }) {
  const loc = useLocation();
  const m = useAccess();
  return canOpen(m, loc.pathname) ? <>{children}</> : <NoAccess path={loc.pathname} />;
}

export function App() {
  const account = currentAccount();
  // MSAL hands back a fresh account object on every call, so effects key on its id; keying on the
  // object re-ran them on every render, and each run re-rendered: an endless loop of API calls.
  const accountId = account?.homeAccountId ?? null;
  const loc = useLocation();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [provider, setProvider] = useState(false);
  // Process mining: which screen is open (signed-in staff only; suppliers have their own app).
  useEffect(() => {
    if ((config.authMode === "dev" || accountId) && !loc.pathname.startsWith("/supplier")) trackView(loc.pathname);
  }, [loc.pathname, accountId]);

  useEffect(() => {
    if (config.authMode !== "dev" && !account) return;
    Promise.all([api.records("issue", "new"), api.records("capa")])
      .then(([newIssues, capas]) => setCounts({
        issues: newIssues.length,
        capa: capas.filter((c) => !["closed", "cancelled"].includes(c.state)).length,
      }))
      .catch(() => {});
  }, [accountId, loc.pathname]);

  // BC deep links default to the environment courageMFG is connected to (a browser can still override it on Integrations).
  useEffect(() => {
    if (config.authMode !== "dev" && !account) return;
    api.me().then((m: any) => {
      setProvider(!!m.provider);
      try {
        if (m.bc?.environment && !localStorage.getItem("bc.environment")) localStorage.setItem("bc.environment", m.bc.environment);
        if (m.bc?.company && !localStorage.getItem("bc.company")) localStorage.setItem("bc.company", m.bc.company);
      } catch {}
    }).catch(() => {});
  }, [accountId]);

  // A Productivity form shared by link: anyone with it can fill it in, without signing in.
  if (loc.pathname.startsWith("/f/")) return <Routes><Route path="/f/:token" element={<PublicFormPage />} /></Routes>;
  // A marketing lead form: public, anyone with the link (or a QR code at a trade show) can fill it in.
  if (loc.pathname.startsWith("/lf/")) return <Routes><Route path="/lf/:token" element={<PublicLeadFormPage />} /></Routes>;
  // E-signature: the signer's emailed link (no account), and checking a signed PDF. Both public.
  if (loc.pathname.startsWith("/sign/")) return <Routes><Route path="/sign/:token" element={<PublicSignPage />} /></Routes>;
  if (loc.pathname === "/verify") return <Routes><Route path="/verify" element={<VerifyPage />} /></Routes>;

  // Suppliers have their own small app with emailed sign-in links; no Microsoft account.
  if (loc.pathname === "/supplier" || loc.pathname.startsWith("/supplier/")) return <Routes><Route path="/supplier/*" element={<SupplierApp />} /></Routes>;

  if (config.authMode !== "dev" && !account) return <SignIn />;
  const name = account?.name ?? localStorage.getItem("devUser") ?? "Dev user";

  // IT provider staff get the service desk and nothing else.
  if (provider) return (
    <div className="provider-frame">
      <header className="provider-top"><Link to="/service-desk" className="pt-brand"><Mark /><span>courageMFG</span></Link><span className="pt-sub">Service desk</span><span style={{ flex: 1 }} /><span>{name}</span></header>
      <Routes>
        <Route path="/service-desk/:number" element={<TicketPage />} />
        <Route path="*" element={<DeskPage />} />
      </Routes>
    </div>
  );

  // The warehouse app runs full screen on handhelds, outside the desktop shell.
  if (loc.pathname === "/wms" || loc.pathname.startsWith("/wms/")) return <Routes><Route path="/wms/*" element={<WmsApp name={name} />} /></Routes>;

  return (
    <Shell name={name} counts={counts}>
      <Guarded>
      <Routes>
        <Route path="/" element={<Home name={name} />} />
        <Route path="/my-work" element={<MyWorkPage />} />
        <Route path="/issues" element={<Records type="issue" />} />
        <Route path="/capas" element={<Records type="capa" />} />
        <Route path="/new" element={<NewIssue />} />
        <Route path="/r/:id" element={<RecordPage />} />
        <Route path="/pareto" element={<ParetoPage />} />
        <Route path="/ontology" element={<OntologyPage />} />
        <Route path="/ontology/t/:id" element={<ObjectTypePage />} />
        <Route path="/ontology/:tab" element={<OntologyPage />} />
        <Route path="/o/:entity/:key" element={<ObjectView />} />
        <Route path="/a/productivity" element={<ProductivityHome />} />
        <Route path="/a/marketing" element={<MarketingHome />} />
        <Route path="/marketing" element={<MarketingHome />} />
        <Route path="/marketing/calendar" element={<CalendarPage />} />
        <Route path="/marketing/campaigns" element={<CampaignsPage />} />
        <Route path="/marketing/campaigns/new" element={<NewCampaignPage />} />
        <Route path="/marketing/campaigns/:number" element={<CampaignPage />} />
        <Route path="/marketing/leads" element={<LeadsPage />} />
        <Route path="/marketing/leads/new" element={<NewLeadPage />} />
        <Route path="/marketing/leads/import" element={<ImportLeadsPage />} />
        <Route path="/marketing/leads/:number" element={<LeadPage />} />
        <Route path="/marketing/forms" element={<FormsPage />} />
        <Route path="/marketing/pages" element={<PagesPage />} />
        <Route path="/marketing/pages/:number" element={<PageEditorPage />} />
        <Route path="/marketing/emails" element={<EmailsPage />} />
        <Route path="/marketing/emails/:number" element={<EmailPage />} />
        <Route path="/marketing/sequences" element={<SequencesPage />} />
        <Route path="/marketing/sequences/:number" element={<SequencePage />} />
        <Route path="/marketing/audiences" element={<AudiencesPage />} />
        <Route path="/marketing/email-settings" element={<EmailSettingsPage />} />
        <Route path="/a/:id" element={<AreaPage />} />
        <Route path="/productivity/templates" element={<TemplatesPage />} />
        <Route path="/productivity/import" element={<ImportPage />} />
        <Route path="/p/:id" element={<TablePage />} />
        <Route path="/p/:id/form/:viewId" element={<FormPage />} />
        <Route path="/accounts" element={<AccountsList />} />
        <Route path="/accounts/new" element={<NewAccount />} />
        <Route path="/accounts/import" element={<ImportAccounts />} />
        <Route path="/accounts/:id" element={<AccountPage />} />
        <Route path="/opportunities" element={<PipelinePage />} />
        <Route path="/opportunities/new" element={<NewOpportunity />} />
        <Route path="/opportunities/:id" element={<OpportunityPage />} />
        <Route path="/quotes/:id" element={<QuotePage />} />
        <Route path="/analytics/sales" element={<SalesAnalyticsPage />} />
        <Route path="/contracts" element={<ContractsPage />} />
        <Route path="/contracts/:id/sign" element={<ContractSignPage />} />
        <Route path="/contracts/:id" element={<ContractPage />} />
        <Route path="/planning" element={<PlanningPage />} />
        <Route path="/planning/inventory" element={<InventoryPage />} />
        <Route path="/inventory/replenishment" element={<ReplenishmentPage />} />
        <Route path="/inventory/counts" element={<CountsPage />} />
        <Route path="/inventory/counts/new" element={<NewCountPage />} />
        <Route path="/inventory/counts/settings" element={<CountSettingsPage />} />
        <Route path="/inventory/counts/:number" element={<CountPage />} />
        <Route path="/planning/dashboard" element={<PlanDashboardPage />} />
        <Route path="/planning/orders" element={<OrderPlannerPage />} />
        <Route path="/cpm" element={<CpmPage />} />
        <Route path="/cpm/:id" element={<CpmVersionPage />} />
        <Route path="/dashboards" element={<DashboardsPage />} />
        <Route path="/dashboards/:id" element={<DashboardPage />} />
        <Route path="/workbooks" element={<WorkbooksPage />} />
        <Route path="/workbooks/:id" element={<WorkbookPage />} />
        <Route path="/item-setups" element={<ItemSetupsPage />} />
        <Route path="/item-setups/:id" element={<ItemSetupPage />} />
        <Route path="/plm/changes" element={<ChangeOrdersPage />} />
        <Route path="/plm/changes/:id" element={<ChangeOrderPage />} />
        <Route path="/plm/import" element={<ImportCadPage />} />
        <Route path="/settings/links" element={<LinksPage />} />
        <Route path="/settings/integrations" element={<IntegrationsPage name={name} />} />
        <Route path="/help" element={<HelpHome />} />
        <Route path="/help/new/:typeId" element={<HelpNew />} />
        <Route path="/help/kb/:id" element={<HelpArticle />} />
        <Route path="/help/:number" element={<HelpTicket />} />
        <Route path="/service-desk" element={<DeskPage />} />
        <Route path="/service-desk/new" element={<NewTicketPage />} />
        <Route path="/service-desk/approvals" element={<ApprovalsPage />} />
        <Route path="/service-desk/report" element={<ReportPage />} />
        <Route path="/service-desk/settings" element={<SettingsPage />} />
        <Route path="/service-desk/:number" element={<TicketPage />} />
        <Route path="/supplier-portal" element={<PortalHomePage />} />
        <Route path="/supplier-portal/orders" element={<PortalOrdersPage />} />
        <Route path="/supplier-portal/po/:no" element={<PortalPoPage />} />
        <Route path="/supplier-portal/suppliers" element={<PortalSuppliersPage />} />
        <Route path="/supplier-portal/suppliers/:vendor" element={<PortalSupplierPage />} />
        <Route path="/supplier-portal/settings" element={<PortalSettingsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/maintenance" element={<WorkOrdersPage />} />
        <Route path="/maintenance/request" element={<RequestPage />} />
        <Route path="/maintenance/new" element={<NewWorkOrderPage />} />
        <Route path="/maintenance/wo/:number" element={<WorkOrderPage />} />
        <Route path="/maintenance/schedule" element={<SchedulePage />} />
        <Route path="/maintenance/assets" element={<AssetsPage />} />
        <Route path="/maintenance/assets/new" element={<NewAssetPage />} />
        <Route path="/maintenance/assets/:number" element={<AssetPage />} />
        <Route path="/maintenance/pm" element={<PmsPage />} />
        <Route path="/maintenance/pm/new" element={<NewPmPage />} />
        <Route path="/maintenance/pm/:number" element={<PmPage />} />
        <Route path="/improve/catalog" element={<CatalogPage />} />
        <Route path="/improve/catalog/:number" element={<CatalogProcessPage />} />
        <Route path="/improve/systems" element={<SystemsPage />} />
        <Route path="/improve/systems/:id" element={<SystemPage />} />
        <Route path="/improve/roadmap" element={<RoadmapPage />} />
        <Route path="/improve/steering" element={<SteeringPage />} />
        <Route path="/improve/steering/:id" element={<MeetingPage />} />
        <Route path="/improve/process-mining" element={<ProcessMiningPage />} />
        <Route path="/improve/process-mining/screens" element={<ScreensPage />} />
        <Route path="/improve/process-mining/p/:id" element={<ProcessPage />} />
        <Route path="/factory" element={<FactoryHome />} />
        <Route path="/factory/requests" element={<FactoryRequests />} />
        <Route path="/factory/new" element={<FactoryNewRequest />} />
        <Route path="/factory/r/:number" element={<FactoryRequest />} />
        <Route path="/factory/tasks" element={<FactoryTasks />} />
        <Route path="/factory/t/:number" element={<FactoryTask />} />
        <Route path="/factory/pipeline" element={<ReleasePipeline />} />
        <Route path="/factory/testing" element={<TestingPage />} />
        <Route path="/factory/docs" element={<DocsHome />} />
        <Route path="/factory/docs/:app" element={<AppDocPage />} />
        <Route path="/processes" element={<ProcessLibrary />} />
        <Route path="/processes/:id" element={<ProcessMapPage />} />
        <Route path="/m/:id" element={<SimpleAppPage />} />
        <Route path="/m/:id/new" element={<SimpleAppNew />} />
        <Route path="/m/:id/:number" element={<SimpleAppRecord />} />
        <Route path="/settings/security" element={<SecurityHome />} />
        <Route path="/settings/security/roles" element={<RolesPage />} />
        <Route path="/settings/security/roles/:id" element={<RolePage />} />
        <Route path="/settings/security/people" element={<PeoplePage />} />
        <Route path="/settings/security/check" element={<CheckPage />} />
        <Route path="/settings/security/changes" element={<ChangesPage />} />
        <Route path="*" element={<div className="page"><div className="card empty">This page doesn't exist or has moved. <Link to="/search">Search for it</Link> or go to <Link to="/">Courage home</Link>.</div></div>} />
      </Routes>
      </Guarded>
    </Shell>
  );
}
